
import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { availableAgents } from '@/utils/a2a/registry/agentRegistry';
import { Brain, Users, Settings, Mail, CheckCircle, XCircle } from 'lucide-react';

export const HierarchicalAgentTree = () => {
  // Remove duplicates by agent ID
  const uniqueAgents = availableAgents.reduce((acc, agent) => {
    if (!acc.find(existing => existing.id === agent.id)) {
      acc.push(agent);
    }
    return acc;
  }, [] as typeof availableAgents);

  // Get BrigAI (Central Orchestrator)
  const brigAI = uniqueAgents.find(agent => agent.id === 'brig-ai' || agent.id === 'executive-agent');

  // Group agents by category
  const recruitingAgents = uniqueAgents.filter(agent => agent.category === 'recruiting');
  const specializedAgents = uniqueAgents.filter(agent => agent.category === 'specialized');
  const emailAgents = uniqueAgents.filter(agent => agent.id.includes('email') && agent.category === 'specialized');

  const AgentCard = ({ agent, isOrchestrator = false }: { agent: any, isOrchestrator?: boolean }) => (
    <Card className={`
      transition-all duration-200 hover:shadow-lg
      ${isOrchestrator 
        ? 'bg-gradient-to-r from-red-500 to-rose-500 text-white border-red-300' 
        : 'bg-card border-border hover:border-red-200'
      }
    `}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className={`text-sm font-semibold ${isOrchestrator ? 'text-white' : 'text-card-foreground'}`}>
            {agent.name}
          </CardTitle>
          <Badge 
            variant={agent.isAvailable ? "default" : "secondary"}
            className={`
              ${agent.isAvailable 
                ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' 
                : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
              }
            `}
          >
            {agent.isAvailable ? (
              <CheckCircle className="w-3 h-3 mr-1" />
            ) : (
              <XCircle className="w-3 h-3 mr-1" />
            )}
            {agent.isAvailable ? 'Active' : 'Inactive'}
          </Badge>
        </div>
        <CardDescription className={`text-xs ${isOrchestrator ? 'text-red-100' : 'text-muted-foreground'}`}>
          {agent.description}
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="space-y-2">
          <div className="flex flex-wrap gap-1">
            {agent.capabilities?.slice(0, 3).map((capability: string) => (
              <Badge 
                key={capability} 
                variant="outline" 
                className={`text-xs ${
                  isOrchestrator 
                    ? 'border-red-200 text-red-100 bg-red-600/20' 
                    : 'border-border text-muted-foreground bg-muted/50'
                }`}
              >
                {capability}
              </Badge>
            ))}
            {agent.capabilities?.length > 3 && (
              <Badge 
                variant="outline" 
                className={`text-xs ${
                  isOrchestrator 
                    ? 'border-red-200 text-red-100 bg-red-600/20' 
                    : 'border-border text-muted-foreground bg-muted/50'
                }`}
              >
                +{agent.capabilities.length - 3}
              </Badge>
            )}
          </div>
          <div className={`text-xs ${isOrchestrator ? 'text-red-200' : 'text-muted-foreground'}`}>
            Max Tasks: {agent.maxConcurrentTasks || 'N/A'}
          </div>
        </div>
      </CardContent>
    </Card>
  );

  const CategorySection = ({ 
    title, 
    icon: Icon, 
    agents, 
    bgColor,
    description 
  }: { 
    title: string, 
    icon: any, 
    agents: any[], 
    bgColor: string,
    description: string 
  }) => (
    <div className={`p-6 rounded-lg border border-border ${bgColor}`}>
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 rounded-lg bg-red-100 dark:bg-red-900/30">
          <Icon className="w-5 h-5 text-red-600 dark:text-red-400" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-foreground">{title}</h3>
          <p className="text-sm text-muted-foreground">{description}</p>
        </div>
        <Badge variant="secondary" className="ml-auto">
          {agents.length} {agents.length === 1 ? 'Agent' : 'Agents'}
        </Badge>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {agents.map((agent) => (
          <AgentCard key={agent.id} agent={agent} />
        ))}
      </div>
    </div>
  );

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center space-y-4">
        <h1 className="text-3xl font-bold bg-gradient-to-r from-red-600 to-rose-600 bg-clip-text text-transparent">
          AI Agent Orchestration Hierarchy
        </h1>
        <p className="text-muted-foreground max-w-2xl mx-auto">
          A comprehensive view of our multi-agent system, from the central orchestrator to specialized domain experts.
        </p>
        <div className="flex justify-center gap-6 text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-red-500"></div>
            Central Orchestrator
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-red-200"></div>
            Function Categories
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-gray-300"></div>
            Individual Agents
          </div>
        </div>
      </div>

      {/* Tier 0: Central Orchestrator */}
      {brigAI && (
        <div className="flex justify-center">
          <div className="w-full max-w-md">
            <div className="text-center mb-4">
              <h2 className="text-xl font-semibold text-foreground mb-2">Central Orchestrator</h2>
              <p className="text-sm text-muted-foreground">
                The master coordinator that manages all agent interactions and task delegation
              </p>
            </div>
            <AgentCard agent={brigAI} isOrchestrator={true} />
          </div>
        </div>
      )}

      <Separator className="my-8" />

      {/* Tier 1: Function Categories */}
      <div className="space-y-6">
        <div className="text-center mb-6">
          <h2 className="text-xl font-semibold text-foreground mb-2">Specialized Agent Categories</h2>
          <p className="text-sm text-muted-foreground">
            Domain-specific agents organized by their primary function and expertise
          </p>
        </div>

        <CategorySection
          title="Recruiting Agents"
          icon={Users}
          agents={recruitingAgents}
          bgColor="bg-red-50 dark:bg-red-950/20"
          description="Specialized in candidate sourcing, screening, and placement processes"
        />

        <CategorySection
          title="Specialized Agents"
          icon={Settings}
          agents={specializedAgents.filter(agent => !agent.id.includes('email'))}
          bgColor="bg-slate-50 dark:bg-slate-950/20"
          description="Domain experts for analytics, scheduling, compliance, and integrations"
        />

        <CategorySection
          title="Email & Communication Agents"
          icon={Mail}
          agents={emailAgents}
          bgColor="bg-gray-50 dark:bg-gray-950/20"
          description="Specialized agents for email processing, composition, and workflow management"
        />
      </div>

      {/* Summary Stats */}
      <div className="mt-8 p-6 bg-muted/50 rounded-lg border border-border">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          <div>
            <div className="text-2xl font-bold text-red-600">{uniqueAgents.length}</div>
            <div className="text-sm text-muted-foreground">Total Agents</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-red-600">{uniqueAgents.filter(a => a.isAvailable).length}</div>
            <div className="text-sm text-muted-foreground">Active Agents</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-red-600">3</div>
            <div className="text-sm text-muted-foreground">Categories</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-red-600">
              {uniqueAgents.reduce((sum, agent) => sum + (agent.maxConcurrentTasks || 0), 0)}
            </div>
            <div className="text-sm text-muted-foreground">Total Capacity</div>
          </div>
        </div>
      </div>
    </div>
  );
};
